import { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import API, { authFetch } from './api';

export default function PropertyDetail() {
  const { propertyId } = useParams();
  const [searchParams] = useSearchParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') === 'buyers' ? 'buyers' : 'details');
  const [buyerMatches, setBuyerMatches] = useState([]);
  const [loadingBuyers, setLoadingBuyers] = useState(false);

  useEffect(() => { loadProperty(); }, [propertyId]);
  useEffect(() => { if (activeTab === 'buyers') loadBuyerMatches(); }, [activeTab, propertyId]);

  const loadProperty = async () => {
    try {
      const res = await authFetch(`${API}/api/properties/${propertyId}`);
      if (res.ok) setProperty(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const loadBuyerMatches = async () => {
    setLoadingBuyers(true);
    try {
      const buyersRes = await authFetch(`${API}/api/buyers`);
      if (!buyersRes.ok) { setLoadingBuyers(false); return; }
      const buyers = await buyersRes.json();

      const matches = [];
      for (const buyer of buyers) {
        const scoreRes = await authFetch(`${API}/api/grades/fit-score/${propertyId}/buyer/${buyer.id}`);
        let scoreData = null;
        if (scoreRes.ok) scoreData = await scoreRes.json();

        let budget = null;
        try {
          const measRes = await authFetch(`${API}/api/measurables/definitions/${buyer.id}`);
          if (measRes.ok) {
            const defs = await measRes.json();
            const budgetDef = defs.find(d => d.name.toLowerCase().includes('budget') || d.name.toLowerCase().includes('max price'));
            if (budgetDef?.target_value) budget = parseFloat(budgetDef.target_value);
          }
        } catch (e) { /* silent */ }

        if (scoreData && scoreData.overall) {
          matches.push({ ...buyer, fit_score: scoreData.overall, tier: scoreData.tier, budget });
        }
      }

      matches.sort((a, b) => b.fit_score - a.fit_score);
      setBuyerMatches(matches);
    } catch (err) { console.error(err); }
    finally { setLoadingBuyers(false); }
  };

  const formatPrice = (p) => p ? `$${Number(p).toLocaleString()}` : 'N/A';

  const getPriceColor = (budget) => {
    if (!budget || !property?.price) return 'text-[#00E676]';
    const ratio = parseFloat(property.price) / budget;
    if (ratio <= 1.0) return 'text-[#00E676]';
    if (ratio <= 1.025) return 'text-yellow-300';
    if (ratio <= 1.05) return 'text-yellow-400';
    if (ratio <= 1.075) return 'text-orange-400';
    if (ratio <= 1.10) return 'text-orange-500';
    return 'text-red-400';
  };

  const getTimelineLabel = (t) => ({ '30_days': '30 days', '60_days': '60 days', '90_days': '90 days', '6_months': '6 months', 'exploring': 'Exploring' }[t] || 'Not set');
  const getTimelineColor = (t) => ({ '30_days': 'text-red-400 font-semibold', '60_days': 'text-orange-400', '90_days': 'text-yellow-400', '6_months': 'text-blue-400' }[t] || 'text-gray-500');
  const getPreApprovalLabel = (s) => ({ 'approved': 'Funding Secured', 'in_process': 'Financing In Progress', 'not_started': 'Not Yet Initiated', 'unknown': 'Unknown' }[s] || 'Unknown');
  const getPreApprovalColor = (s) => ({ 'approved': 'text-[#00E676]', 'in_process': 'text-yellow-400', 'not_started': 'text-orange-400' }[s] || 'text-gray-500');
  const getMotivationLabel = (m) => ({ 'must_move': 'Immediate Need', 'want_to_move': 'Active Search', 'motivated': 'Evaluating Options', 'casually_looking': 'Exploring Market' }[m] || 'Not set');
  const getMotivationColor = (m) => ({ 'must_move': 'text-red-400 font-semibold', 'want_to_move': 'text-yellow-400', 'motivated': 'text-blue-400' }[m] || 'text-gray-500');

  const listingTypeBadge = (lt) => {
    if (lt === 'for_sale')           return { label: 'FOR SALE',     cls: 'bg-green-900/50 text-green-300 border-green-600/40' };
    if (lt === 'for_sale_and_lease') return { label: 'SALE & LEASE', cls: 'bg-orange-900/50 text-orange-300 border-orange-600/40' };
    if (lt === 'for_lease')          return { label: 'FOR LEASE',    cls: 'bg-blue-900/50 text-blue-300 border-blue-600/40' };
    return null;
  };

  const getTierBadge = (tier) => {
    const colors = {
      'ELITE FIT': 'bg-emerald-900/70 text-emerald-300 border-emerald-500/30',
      'STRONG FIT': 'bg-green-900/50 text-green-300 border-green-500/20',
      'GOOD FIT': 'bg-yellow-900/40 text-yellow-300 border-yellow-500/20',
      'MARGINAL FIT': 'bg-orange-900/40 text-orange-300 border-orange-500/20',
      'POOR FIT': 'bg-red-900/40 text-red-300 border-red-500/20',
    };
    return <span className={`px-2 py-0.5 rounded text-xs font-medium border ${colors[tier] || 'bg-gray-800 text-gray-400'}`}>{tier}</span>;
  };

  if (loading) return <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center"><div className="text-[#00D4FF]">Loading...</div></div>;
  if (!property) return <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center"><div className="text-red-400">Property not found</div></div>;

  return (
    <div className="min-h-screen bg-[#0A0E27] pt-8 pb-12">
      <div className="max-w-5xl mx-auto px-4 md:px-6">
        <Link to="/agent-board" className="text-[#00D4FF] text-sm hover:text-[#00E676] mb-2 inline-block">← Back to Master Board</Link>
        <h1 className="text-xl md:text-2xl font-bold text-white">{property.address}</h1>
        <p className="text-gray-400">{property.city}, {property.state} {property.zip}</p>
        {property.mls_number && <p className="text-gray-500 text-sm">Listing ID: {property.mls_number}</p>}
        <div className="flex flex-wrap items-center gap-2 mt-3">
          {(() => { const lt = listingTypeBadge(property.listing_type); return lt && (
            <span className={`px-2.5 py-1 rounded text-xs font-bold border ${lt.cls}`}>{lt.label}</span>
          ); })()}
          {property.listing_status && (
            <span className={`px-2.5 py-1 rounded text-xs font-medium border capitalize ${
              property.listing_status === 'active' ? 'bg-green-900/40 text-green-300 border-green-500/30' :
              property.listing_status === 'pending' ? 'bg-yellow-900/40 text-yellow-300 border-yellow-500/30' :
              property.listing_status === 'leased' ? 'bg-blue-900/40 text-blue-300 border-blue-500/30' :
              'bg-red-900/40 text-red-300 border-red-500/30'
            }`}>{property.listing_status}</span>
          )}
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-1 mt-6 mb-6 border-b border-[#546E7A]/30">
          <button onClick={() => setActiveTab('details')}
            className={`px-5 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'details' ? 'text-[#00D4FF] border-[#00D4FF]' : 'text-gray-400 border-transparent hover:text-white'}`}>
            Property Details
          </button>
          <button onClick={() => setActiveTab('buyers')}
            className={`px-5 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'buyers' ? 'text-[#00D4FF] border-[#00D4FF]' : 'text-gray-400 border-transparent hover:text-white'}`}>
            Matching Clients {buyerMatches.length > 0 && `(${buyerMatches.length})`}
          </button>
        </div>

        {/* Details Tab */}
        {activeTab === 'details' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Property Details */}
              <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6">
                <h3 className="text-white font-semibold mb-4">Property Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm break-words">
                  {property.property_name && <div className="col-span-2"><span className="text-gray-500">Property Name:</span><br/><span className="text-white">{property.property_name}</span></div>}
                  <div className="col-span-2"><span className="text-gray-500">Address:</span><br/><span className="text-white">{property.address}</span></div>
                  <div className="col-span-2"><span className="text-gray-500">City/State/ZIP:</span><br/><span className="text-white">{[property.city, property.state, property.zip].filter(Boolean).join(', ')}</span></div>
                  {property.property_type && <div><span className="text-gray-500">Type:</span><br/><span className="text-white capitalize">{property.property_type.replace('_', ' ')}</span></div>}
                  {property.property_subtype && <div><span className="text-gray-500">Subtype:</span><br/><span className="text-white">{property.property_subtype}</span></div>}
                  {property.listing_status && <div><span className="text-gray-500">Status:</span><br/><span className={`font-medium capitalize ${property.listing_status === 'active' ? 'text-green-400' : property.listing_status === 'pending' ? 'text-yellow-400' : property.listing_status === 'leased' ? 'text-blue-400' : 'text-red-400'}`}>{property.listing_status}</span></div>}
                  <div><span className="text-gray-500">Price:</span><br/><span className="text-[#00E676] font-bold text-lg">{formatPrice(property.price)}</span></div>
                  <div><span className="text-gray-500">Price/Sqft:</span><br/><span className="text-white">{(property.rentable_sqft || property.sqft) && property.price ? `$${Math.round(property.price / (property.rentable_sqft || property.sqft))}` : 'N/A'}</span></div>
                  {property.mls_number && <div className="col-span-2"><span className="text-gray-500">Listing ID:</span><br/><span className="text-white">{property.mls_number}</span></div>}
                </div>
                {property.listing_url && (
                  <a href={property.listing_url} target="_blank" rel="noopener noreferrer"
                    className="inline-block mt-4 px-4 py-2 bg-[#243B55] text-[#00D4FF] rounded-lg text-sm font-medium hover:bg-[#2d4a6a]">
                    View Full Listing →
                  </a>
                )}
              </div>

              {/* Building Specs */}
              {(property.rentable_sqft || property.usable_sqft || property.available_sqft || property.sqft || property.ceiling_height_ft != null || property.stories != null || property.loading_docks != null || property.building_class || property.freight_elevator || property.lot_size_acres != null || property.number_of_units != null || property.office_percentage != null || property.tenancy_type) && (
                <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6">
                  <h3 className="text-white font-semibold mb-4">Building Specs</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm break-words">
                    {property.rentable_sqft != null && <div><span className="text-gray-500">Rentable Sqft:</span><br/><span className="text-white">{property.rentable_sqft.toLocaleString()}</span></div>}
                    {property.usable_sqft != null && <div><span className="text-gray-500">Usable Sqft:</span><br/><span className="text-white">{property.usable_sqft.toLocaleString()}</span></div>}
                    {property.available_sqft != null && <div><span className="text-gray-500">Available Sqft:</span><br/><span className="text-white">{property.available_sqft.toLocaleString()}</span></div>}
                    {property.sqft != null && <div><span className="text-gray-500">Total Sqft:</span><br/><span className="text-white">{property.sqft.toLocaleString()}</span></div>}
                    {property.ceiling_height_ft != null && <div><span className="text-gray-500">Ceiling Height:</span><br/><span className="text-white">{Number(property.ceiling_height_ft).toFixed(1)} ft</span></div>}
                    {property.stories != null && <div><span className="text-gray-500">Stories:</span><br/><span className="text-white">{property.stories}</span></div>}
                    {property.loading_docks != null && property.loading_docks > 0 && <div><span className="text-gray-500">Loading Docks:</span><br/><span className="text-white">{property.loading_docks}</span></div>}
                    {property.building_class && <div><span className="text-gray-500">Building Class:</span><br/><span className="text-white">Class {property.building_class}</span></div>}
                    {property.freight_elevator && <div><span className="text-gray-500">Freight Elevator:</span><br/><span className="text-[#00E676]">Yes</span></div>}
                    {property.lot_size_acres != null && <div><span className="text-gray-500">Lot Size:</span><br/><span className="text-white">{Number(property.lot_size_acres).toFixed(2)} acres</span></div>}
                    {property.number_of_units != null && <div><span className="text-gray-500">Number of Units:</span><br/><span className="text-white">{property.number_of_units}</span></div>}
                    {property.office_percentage != null && <div><span className="text-gray-500">Office %:</span><br/><span className="text-white">{Number(property.office_percentage).toFixed(0)}%</span></div>}
                    {property.tenancy_type && <div><span className="text-gray-500">Tenancy:</span><br/><span className="text-white capitalize">{property.tenancy_type === 'single' ? 'Single-tenant' : property.tenancy_type === 'multi' ? 'Multi-tenant' : property.tenancy_type}</span></div>}
                  </div>
                </div>
              )}
            </div>

            {/* Lease & Investment */}
            {(property.lease_type || property.lease_rate_sqft != null || property.cam_charges != null || property.ti_allowance != null || property.cap_rate != null || property.noi != null || property.occupancy_rate != null || property.annual_taxes != null || property.investment_highlights || (property.original_list_price != null && property.original_list_price !== property.price)) && (
              <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6">
                <h3 className="text-white font-semibold mb-4">Lease & Investment</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 text-sm break-words">
                  {property.lease_type && <div><span className="text-gray-500">Lease Type:</span><br/><span className="text-white capitalize">{property.lease_type.replace('_', ' ')}</span></div>}
                  {property.lease_rate_sqft != null && <div><span className="text-gray-500">Lease Rate:</span><br/><span className="text-white">${Number(property.lease_rate_sqft).toLocaleString()}/sqft/yr</span></div>}
                  {property.cam_charges != null && <div><span className="text-gray-500">CAM Charges:</span><br/><span className="text-white">${Number(property.cam_charges).toLocaleString()}/sqft</span></div>}
                  {property.ti_allowance != null && <div><span className="text-gray-500">TI Allowance:</span><br/><span className="text-white">${Number(property.ti_allowance).toLocaleString()}</span></div>}
                  {property.cap_rate != null && <div><span className="text-gray-500">Cap Rate:</span><br/><span className="text-white">{Number(property.cap_rate).toFixed(1)}%</span></div>}
                  {property.noi != null && <div><span className="text-gray-500">NOI:</span><br/><span className="text-white">${Number(property.noi).toLocaleString()}</span></div>}
                  {property.occupancy_rate != null && <div><span className="text-gray-500">Occupancy:</span><br/><span className="text-white">{Number(property.occupancy_rate).toFixed(1)}%</span></div>}
                  {property.annual_taxes != null && <div><span className="text-gray-500">Annual Taxes:</span><br/><span className="text-white">${Number(property.annual_taxes).toLocaleString()}</span></div>}
                  {property.original_list_price != null && property.original_list_price !== property.price && <div><span className="text-gray-500">Original List:</span><br/><span className="text-white">{formatPrice(property.original_list_price)}</span></div>}
                </div>
                {property.investment_highlights && (
                  <div className="mt-4">
                    <span className="text-gray-500 text-sm">Investment Highlights:</span>
                    <p className="text-white text-sm mt-1 whitespace-pre-wrap">{property.investment_highlights}</p>
                  </div>
                )}
              </div>
            )}

            {/* Industrial Features */}
            {(property.drive_in_doors != null || property.rail_access || property.column_spacing || property.floor_load_capacity || property.power_type || property.construction_type || property.roof_type) && (
              <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6">
                <h3 className="text-white font-semibold mb-4">Industrial Features</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 text-sm break-words">
                  {property.drive_in_doors != null && <div><span className="text-gray-500">Drive-in Doors:</span><br/><span className="text-white">{property.drive_in_doors}</span></div>}
                  {property.rail_access && <div><span className="text-gray-500">Rail Access:</span><br/><span className="text-[#00E676]">Yes</span></div>}
                  {property.column_spacing && <div><span className="text-gray-500">Column Spacing:</span><br/><span className="text-white">{property.column_spacing}</span></div>}
                  {property.floor_load_capacity && <div><span className="text-gray-500">Floor Load:</span><br/><span className="text-white">{property.floor_load_capacity}</span></div>}
                  {property.power_type && <div><span className="text-gray-500">Power:</span><br/><span className="text-white">{property.power_type}</span></div>}
                  {property.construction_type && <div><span className="text-gray-500">Construction:</span><br/><span className="text-white">{property.construction_type}</span></div>}
                  {property.roof_type && <div className="md:col-span-2"><span className="text-gray-500">Roof:</span><br/><span className="text-white">{property.roof_type}</span></div>}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Parking & Site Access */}
              {(property.parking_spaces != null || property.parking_ratio != null || property.frontage_ft != null || property.traffic_count != null || property.cross_streets) && (
                <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6">
                  <h3 className="text-white font-semibold mb-4">Parking & Site Access</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm break-words">
                    {property.parking_spaces != null && <div><span className="text-gray-500">Parking Spaces:</span><br/><span className="text-white">{property.parking_spaces}</span></div>}
                    {property.parking_ratio != null && <div><span className="text-gray-500">Parking Ratio:</span><br/><span className="text-white">{Number(property.parking_ratio).toFixed(1)} per 1000 sqft</span></div>}
                    {property.frontage_ft != null && <div><span className="text-gray-500">Frontage:</span><br/><span className="text-white">{property.frontage_ft.toLocaleString()} ft</span></div>}
                    {property.traffic_count != null && <div><span className="text-gray-500">Traffic Count:</span><br/><span className="text-white">{property.traffic_count.toLocaleString()} veh/day</span></div>}
                    {property.cross_streets && <div className="col-span-2"><span className="text-gray-500">Cross Streets:</span><br/><span className="text-white">{property.cross_streets}</span></div>}
                  </div>
                </div>
              )}

              {/* Zoning & Compliance */}
              {(property.zoning || property.ada_compliant || property.sprinkler_system || property.environmental_status) && (
                <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6">
                  <h3 className="text-white font-semibold mb-4">Zoning & Compliance</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm break-words">
                    {property.zoning && <div><span className="text-gray-500">Zoning:</span><br/><span className="text-white capitalize">{property.zoning.replace('_', ' ')}</span></div>}
                    {property.ada_compliant && <div><span className="text-gray-500">ADA Compliant:</span><br/><span className="text-[#00E676]">Yes</span></div>}
                    {property.sprinkler_system && <div><span className="text-gray-500">Sprinkler System:</span><br/><span className="text-[#00E676]">Yes</span></div>}
                    {property.environmental_status && <div className="col-span-2"><span className="text-gray-500">Environmental:</span><br/><span className="text-white">{property.environmental_status}</span></div>}
                  </div>
                </div>
              )}
            </div>

            {/* Condition & Systems */}
            {(property.year_built || property.year_renovated || property.cooling || property.heating || property.building_systems || property.building_amenities) && (
              <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6">
                <h3 className="text-white font-semibold mb-4">Condition & Systems</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3 break-words">
                  {property.year_built && <div><span className="text-gray-500">Year Built:</span><br/><span className="text-white">{property.year_built}</span></div>}
                  {property.year_renovated && <div><span className="text-gray-500">Year Renovated:</span><br/><span className="text-white">{property.year_renovated}</span></div>}
                  {property.cooling && <div><span className="text-gray-500">Cooling:</span><br/><span className="text-white">{property.cooling}</span></div>}
                  {property.heating && <div><span className="text-gray-500">Heating:</span><br/><span className="text-white">{property.heating}</span></div>}
                </div>
                {property.building_systems && (
                  <div className="mb-3">
                    <span className="text-gray-500 text-sm">Building Systems:</span>
                    <p className="text-white text-sm mt-1 whitespace-pre-wrap">{property.building_systems}</p>
                  </div>
                )}
                {property.building_amenities && (
                  <div>
                    <span className="text-gray-500 text-sm">Building Amenities:</span>
                    <p className="text-white text-sm mt-1 whitespace-pre-wrap">{property.building_amenities}</p>
                  </div>
                )}
              </div>
            )}

            {/* Location */}
            {(property.county || property.business_park) && (
              <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6">
                <h3 className="text-white font-semibold mb-4">Location</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm break-words">
                  {property.county && <div><span className="text-gray-500">County:</span><br/><span className="text-white">{property.county}</span></div>}
                  {property.business_park && <div><span className="text-gray-500">Business Park:</span><br/><span className="text-white">{property.business_park}</span></div>}
                </div>
              </div>
            )}

            {/* Listing Broker */}
            {(property.listing_broker_name || property.listing_broker_company || property.listing_broker_phone || property.listing_broker_email) && (
              <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6">
                <h3 className="text-white font-semibold mb-4">Listing Broker</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  {property.listing_broker_name && <div><span className="text-gray-500">Name:</span><br/><span className="text-white">{property.listing_broker_name}</span></div>}
                  {property.listing_broker_company && <div><span className="text-gray-500">Company:</span><br/><span className="text-white">{property.listing_broker_company}</span></div>}
                  {property.listing_broker_phone && <div><span className="text-gray-500">Phone:</span><br/><a href={`tel:${property.listing_broker_phone}`} className="text-[#00D4FF] hover:text-[#00E676]">{property.listing_broker_phone}</a></div>}
                  {property.listing_broker_email && <div><span className="text-gray-500">Email:</span><br/><a href={`mailto:${property.listing_broker_email}`} className="text-[#00D4FF] hover:text-[#00E676] break-all">{property.listing_broker_email}</a></div>}
                </div>
              </div>
            )}

            {/* Description */}
            {property.description && (
              <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6">
                <h3 className="text-white font-semibold mb-3">Description</h3>
                <p className="text-gray-200 text-sm whitespace-pre-wrap leading-relaxed">{property.description}</p>
              </div>
            )}
          </div>
        )}

        {/* Buyers Tab */}
        {activeTab === 'buyers' && (
          <div>
            {loadingBuyers ? (
              <div className="text-center py-12"><div className="text-[#00D4FF]">Loading client matches...</div></div>
            ) : buyerMatches.length === 0 ? (
              <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 text-center py-12">
                <h3 className="text-white font-semibold text-lg mb-2">Clients who match this listing</h3>
                <p className="text-gray-400">No clients currently match this listing's profile.</p>
              </div>
            ) : (
              <>
                <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 mb-4">
                  <h3 className="text-white font-semibold text-lg mb-1">Clients who match this listing ({buyerMatches.length})</h3>
                  <p className="text-gray-400 text-sm">
                    {buyerMatches.length} client{buyerMatches.length !== 1 ? 's' : ''} matched to
                    <span className="text-white font-medium"> {property.address}</span>
                    <span className="text-gray-500"> — ranked by fit score</span>
                  </p>
                </div>
                <div className="hidden md:block bg-[#1A2744] rounded-xl border border-[#546E7A]/30 overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#546E7A]/30">
                        <th className="text-left px-4 py-3 text-sm font-medium text-[#B0BEC5]">Client</th>
                        <th className="text-center px-4 py-3 text-sm font-medium text-[#B0BEC5]">Fit Score</th>
                        <th className="text-center px-4 py-3 text-sm font-medium text-[#B0BEC5]">Tier</th>
                        <th className="text-center px-4 py-3 text-sm font-medium text-[#B0BEC5]">Price vs Budget</th>
                        <th className="text-center px-4 py-3 text-sm font-medium text-[#B0BEC5]">Timeline</th>
                        <th className="text-center px-4 py-3 text-sm font-medium text-[#B0BEC5]">Financial Status</th>
                        <th className="text-center px-4 py-3 text-sm font-medium text-[#B0BEC5]">Urgency</th>
                        <th className="text-center px-4 py-3 text-sm font-medium text-[#B0BEC5]">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {buyerMatches.map((buyer, i) => (
                        <tr key={buyer.id} className={`border-b border-[#546E7A]/10 hover:bg-[#243B55]/30 transition-colors ${i % 2 === 1 ? 'bg-[#0A0E27]/20' : ''}`}>
                          <td className="px-4 py-3">
                            <div className="text-white font-medium text-sm">{buyer.name}</div>
                            <div className="text-gray-500 text-xs">{buyer.email}</div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-white font-bold">{Math.round((buyer.fit_score / 9) * 100)}%</span>
                          </td>
                          <td className="px-4 py-3 text-center">{getTierBadge(buyer.tier)}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`font-bold ${getPriceColor(buyer.budget)}`}>{formatPrice(property.price)}</span>
                            {buyer.budget && <div className="text-gray-500 text-xs">Budget: {formatPrice(buyer.budget)}</div>}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`text-sm ${getTimelineColor(buyer.timeline)}`}>{getTimelineLabel(buyer.timeline)}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`text-sm ${getPreApprovalColor(buyer.pre_approval_status)}`}>{getPreApprovalLabel(buyer.pre_approval_status)}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`text-sm ${getMotivationColor(buyer.motivation)}`}>{getMotivationLabel(buyer.motivation)}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Link to={`/properties/${propertyId}/buyer/${buyer.id}`}
                              className="px-3 py-1.5 bg-[#243B55] text-[#00D4FF] text-xs font-medium rounded-lg hover:bg-[#2d4a6a] transition-colors whitespace-nowrap">
                              Full Eval
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Mobile cards */}
                <div className="md:hidden space-y-3">
                  {buyerMatches.map((buyer) => (
                    <div key={buyer.id} className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4">
                      <div className="flex justify-between items-start gap-3 mb-3">
                        <div className="min-w-0">
                          <div className="text-white font-medium text-sm truncate">{buyer.name}</div>
                          <div className="text-gray-500 text-xs truncate">{buyer.email}</div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-white font-bold">{Math.round((buyer.fit_score / 9) * 100)}%</div>
                          <div className="mt-1">{getTierBadge(buyer.tier)}</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                        <div>
                          <div className="text-gray-500">Price vs Budget</div>
                          <div className={`font-bold ${getPriceColor(buyer.budget)}`}>{formatPrice(property.price)}</div>
                          {buyer.budget && <div className="text-gray-500 text-[11px]">Budget: {formatPrice(buyer.budget)}</div>}
                        </div>
                        <div>
                          <div className="text-gray-500">Timeline</div>
                          <div className={getTimelineColor(buyer.timeline)}>{getTimelineLabel(buyer.timeline)}</div>
                        </div>
                        <div>
                          <div className="text-gray-500">Financial Status</div>
                          <div className={getPreApprovalColor(buyer.pre_approval_status)}>{getPreApprovalLabel(buyer.pre_approval_status)}</div>
                        </div>
                        <div>
                          <div className="text-gray-500">Urgency</div>
                          <div className={getMotivationColor(buyer.motivation)}>{getMotivationLabel(buyer.motivation)}</div>
                        </div>
                      </div>
                      <Link to={`/properties/${propertyId}/buyer/${buyer.id}`}
                        className="block text-center px-3 py-2 bg-[#243B55] text-[#00D4FF] text-xs font-medium rounded-lg hover:bg-[#2d4a6a] transition-colors">
                        Full Eval
                      </Link>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
